import React from 'react';
import { StyleProp, StyleSheet, View, ViewStyle } from 'react-native';

import type { MapMarkerProfile } from '@components/Explorer/Markers';
import {
  DEFAULT_SEARCH_TARGETS,
  EMPTY_GROUPS,
  EMPTY_USERS,
} from '@components/Explorer/SearchDefaults';
import { SearchHeader } from '@components/Explorer/SearchHeader';
import { ExplorerItem, getExplorerItems, rankExplorerItems } from '@components/Explorer/explore';
import { filterUsers } from '@components/Explorer/search';
import type { SearchTargets, SelectedSearchFilter } from '@components/Explorer/search';
import { useExplorerSearch } from '@components/Explorer/useExplorerSearch';
import { getMapRegion } from '@helpers/Map';
import type { MapInitialRegion } from '@helpers/Map';
import { Group, Plan, User } from '@types';

type ExplorerSearchListProps = {
  plans: Plan[];
  groups?: Group[];
  users?: User[];
  searchGroupIds?: string[];
  searchUserIds?: string[];
  header?: React.ReactNode;
  getPlanDisplayProfile: (plan: Plan) => MapMarkerProfile;
  initialRegion?: MapInitialRegion;
  /** Renders the ranked Explorer items for non-native-map platforms. */
  renderContent: (items: ExplorerItem[]) => React.ReactNode;
  onSearchFiltersChange?: (filters: SelectedSearchFilter[]) => void;
  searchTargets?: SearchTargets;
  style?: StyleProp<ViewStyle>;
};

export function ExplorerSearchList({
  plans,
  groups = EMPTY_GROUPS,
  users = EMPTY_USERS,
  searchGroupIds,
  searchUserIds,
  getPlanDisplayProfile,
  initialRegion,
  header,
  onSearchFiltersChange,
  renderContent,
  searchTargets = DEFAULT_SEARCH_TARGETS,
  style,
}: ExplorerSearchListProps) {
  const search = useExplorerSearch({
    plans,
    groups,
    users,
    searchTargets,
    searchGroupIds,
    searchUserIds,
  });
  const initialMapRegion = React.useMemo(() => getMapRegion(initialRegion), [initialRegion]);
  const visibleUsers = React.useMemo(
    () => filterUsers(users, search.selectedFilters, search.displayMode),
    [users, search.selectedFilters, search.displayMode]
  );
  const rankedItems = React.useMemo(
    () =>
      rankExplorerItems(
        getExplorerItems({
          plans: search.filteredPlans,
          users: visibleUsers,
          getPlanDisplayProfile,
        }),
        initialMapRegion
      ),
    [getPlanDisplayProfile, initialMapRegion, search.filteredPlans, visibleUsers]
  );

  React.useEffect(() => {
    onSearchFiltersChange?.(search.selectedFilters);
  }, [onSearchFiltersChange, search.selectedFilters]);

  return (
    <View style={[styles.searchListContainer, style]}>
      <SearchHeader
        header={header}
        searchText={search.searchText}
        selectedFilters={search.selectedFilters}
        displayMode={search.displayMode}
        dateRange={search.dateRange}
        usersLoading={search.usersLoading}
        groupsLoading={search.groupsLoading}
        placesLoading={search.placesLoading}
        placeSelectionLoading={search.placeSelectionLoading}
        searchTargets={searchTargets}
        visibleUserResults={search.visibleUserResults}
        visibleGroupResults={search.visibleGroupResults}
        placeResults={search.placeResults}
        setSearchText={search.setSearchText}
        setSelectedFilters={search.setSelectedFilters}
        setDisplayMode={search.setDisplayMode}
        setDateRange={search.setDateRange}
        selectFilter={search.selectFilter}
      />
      <View style={styles.searchListContent}>{renderContent(rankedItems)}</View>
    </View>
  );
}

const styles = StyleSheet.create({
  searchListContainer: {
    flex: 1,
    width: '100%',
  },
  searchListContent: {
    flex: 1,
    width: '100%',
  },
});
